import { useCallback, useEffect, useState } from "react";
import { push as pushApi, type PushDevice } from "../api/client";
import { t } from "../i18n/en";

/** "Safari on iPhone", from a user agent, without a library. Order matters: Edge and iOS Chrome/Firefox
 *  all contain "Safari"/"Chrome"; installed iOS web apps omit "Safari/" entirely; iPads report as Mac. */
export function describeDevice(ua: string | null): string {
  if (!ua) return t.devices.unknown;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /CriOS|Chrome\//.test(ua)
      ? "Chrome"
      : /FxiOS|Firefox\//.test(ua)
        ? "Firefox"
        : /AppleWebKit/.test(ua)
          ? "Safari"
          : t.devices.browser;
  const os = /iPhone/.test(ua)
    ? "iPhone"
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && /Mobile\//.test(ua))
      ? "iPad"
      : /Android/.test(ua)
        ? "Android"
        : /Macintosh/.test(ua)
          ? "Mac"
          : /Windows/.test(ua)
            ? "Windows"
            : /Linux/.test(ua)
              ? "Linux"
              : "";
  return os ? t.devices.on(browser, os) : browser;
}

const dateLabel = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

/**
 * Every device that receives this account's alerts.
 *
 * iOS creates a new subscription each time the app is reinstalled, and old ones linger until the push
 * service finally rejects them — so "why does my old phone still buzz?" needs an answer here.
 */
export default function DeviceList({ onRemoveCurrent }: { onRemoveCurrent: () => Promise<void> }) {
  const [devices, setDevices] = useState<PushDevice[] | null>(null);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const list = await pushApi.devices().catch(() => ({ subscriptions: [] as PushDevice[] }));
    setDevices(list.subscriptions);
    // Identify this browser by sending its own endpoint; endpoints never come back from the server.
    try {
      const registration = await navigator.serviceWorker?.ready;
      const sub = await registration?.pushManager.getSubscription();
      setCurrentId(sub ? (await pushApi.currentDevice(sub.endpoint)).id : null);
    } catch {
      setCurrentId(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!devices || devices.length === 0) return null;

  async function remove(device: PushDevice) {
    setBusyId(device.id);
    try {
      if (device.id === currentId) {
        // This browser: unsubscribe it too, or the UI would claim it's off while it still receives.
        await onRemoveCurrent();
      } else {
        await pushApi.removeDevice(device.id);
      }
      await load();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="devices">
      <h3>{t.devices.title}</h3>
      {devices.map((d) => (
        <div key={d.id} className="device-row">
          <div className="card-main">
            <div className="card-title">
              {describeDevice(d.user_agent)}
              {d.id === currentId && <span className="badge">{t.devices.thisDevice}</span>}
            </div>
            <div className="card-sub">
              {[
                t.devices.added(dateLabel(d.created_at)),
                d.last_success_at ? t.devices.lastDelivered(dateLabel(d.last_success_at)) : t.devices.neverDelivered,
                d.failure_count > 0 ? t.devices.failures(d.failure_count) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          <button className="compact secondary" onClick={() => void remove(d)} disabled={busyId === d.id}>
            {t.devices.remove}
          </button>
        </div>
      ))}
    </div>
  );
}
